import React from "react";
import { DragDropContext, Draggable, DraggableProvided, Droppable, DraggingStyle, DropResult, NotDraggingStyle } from "react-beautiful-dnd";

import { StyledIcon } from "@cofano/cck/Menu/Styled";

import { Box, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";

import { convertedIcon } from "components/icon/IconUtils";
import { useConfirm } from "components/hooks/useConfirm";

import { Language } from "imports/Utils";

import { AdminNavigationItem } from "view/admin/utils/admin.types";

const getItemStyle = (draggableStyle?: DraggingStyle | NotDraggingStyle): React.CSSProperties => ({
	...draggableStyle,
	userSelect: "none",
});

interface AdminNavigationEditableListProps {
	currentItems: AdminNavigationItem[];
	onAddDropdownItem: (item: AdminNavigationItem) => void;
	onEdit: (item: AdminNavigationItem) => void;
	onDelete: (item: AdminNavigationItem) => void;
	isSubmenu?: boolean;
	setItems: (items: AdminNavigationItem[]) => void;
}

const AdminNavigationEditableList = ({ currentItems, onAddDropdownItem, onEdit, onDelete, isSubmenu, setItems }: AdminNavigationEditableListProps) => {
	const onDragEnd = (result: DropResult) => {
		if (!result.destination) {
			return;
		}

		const newItems = [ ...currentItems ];
		const item = newItems[result.source.index];

		newItems.splice(result.source.index, 1);
		newItems.splice(result.destination.index, 0, item);

		setItems(newItems);
	};
	const setChildItems = (index: number, items: AdminNavigationItem[]) => {
		const newItems = [ ...currentItems ];
		newItems[index].children = items;
		setItems(newItems);
	};
	return (
		<DragDropContext onDragEnd={onDragEnd}>
			<Droppable droppableId="droppable">
				{(provided, snapshot) => (
					<Box
						{...provided.droppableProps}
						ref={provided.innerRef}
						sx={{ bgcolor: snapshot.isDraggingOver ? "action.hover" : "transparent", width: "100%" }}>
						{currentItems.map((item, index) => (
							<Draggable key={item.uniqueId} draggableId={item.uniqueId ?? String(index)} index={index}>
								{(p2, s2) => (
									<div>
										<div ref={p2.innerRef} {...p2.draggableProps} style={getItemStyle(p2.draggableProps.style)}>
											<ListItem
												item={item}
												provided={p2}
												isDragging={s2.isDragging}
												onAddDropdownItem={onAddDropdownItem}
												onEdit={onEdit}
												onDelete={onDelete}
												isSubmenu={isSubmenu}
												setItems={items => setChildItems(index, items)}
											/>
										</div>
									</div>
								)}
							</Draggable>
						))}

						{provided.placeholder}
					</Box>
				)}
			</Droppable>
		</DragDropContext>
	);
};

type ListItemProps = {
	item: AdminNavigationItem;
	provided: DraggableProvided;
	isDragging?: boolean;
	onAddDropdownItem: (item: AdminNavigationItem) => void;
	onEdit: (item: AdminNavigationItem) => void;
	onDelete: (item: AdminNavigationItem) => void;
	isSubmenu?: boolean;
	setItems: (items: AdminNavigationItem[]) => void;
};

const ListItem = ({ item, provided, isDragging, onAddDropdownItem, onEdit, onDelete, isSubmenu, setItems }: ListItemProps) => {

	const confirm = useConfirm();

	return (
		<Box
			sx={{
				alignItems: "center",
				backgroundColor: isDragging ? "action.selected" : "action.hover",
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				mb: 1,
				p: 0.75,
				width: "100%",
			}}>
			<Box sx={{ display: "flex", width: "100%" }}>
				<Box sx={{ alignItems: "center", display: "flex" }}>
					<Box
						component="span"
						{...provided.dragHandleProps}
						sx={{
							alignItems: "center",
							borderRadius: "30px",
							display: "flex",
							height: 23,
							justifyContent: "center",
							width: 23,

							"&:hover": {
								bgcolor: "action.selected",
								cursor: "pointer",
							},
						}}>
						<DragIndicatorIcon fontSize="small" />
					</Box>
				</Box>

				<StyledIcon>
					<i className={`fa ${convertedIcon(item.icon)}`} />
				</StyledIcon>

				<Box sx={{ alignItems: "center", display: "flex", flexGrow: 1, fontSize: 13, fontWeight: 600, px: 1 }}>
					{Language(item.languageKey)}
				</Box>

				<Box sx={{ alignItems: "center", display: "flex" }}>
					<IconButton size="small" onClick={() => onAddDropdownItem(item)} hidden={isSubmenu}>
						<AddIcon fontSize="small" />
					</IconButton>

					<IconButton size="small" onClick={() => onEdit(item)}>
						<EditIcon fontSize="small" />
					</IconButton>

					<IconButton
						size="small"
						onClick={() => {
							confirm({
								type: "DELETE",
							}).then(() => {
								onDelete(item);
							});
						}}>
						<DeleteIcon fontSize="small" />
					</IconButton>
				</Box>
			</Box>

			<Box
				sx={{
					boxSizing: "border-box",
					pl: (item.children ?? []).length === 0 ? 0 : 2,
					pt: (item.children ?? []).length === 0 ? 0 : 1,
					pr: (item.children ?? []).length === 0 ? 0 : 1,
					width: "100%",
				}}>
				<AdminNavigationEditableList
					currentItems={item.children ?? []}
					onAddDropdownItem={onAddDropdownItem}
					onEdit={onEdit}
					onDelete={onDelete}
					isSubmenu={true}
					setItems={setItems} />
			</Box>
		</Box>
	);
};

export default AdminNavigationEditableList;
